<script setup>
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ProjectBaselinesAPI from 'dashboard/api/projectBaselines';
import Button from 'dashboard/components-next/button/Button.vue';
import ProjectEmptyState from './ProjectEmptyState.vue';

const props = defineProps({
  projectId: {
    type: [String, Number],
    required: true,
  },
  // When true, the header acts as a disclosure toggle for the body.
  collapsible: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const isOpen = ref(true);
const toggleOpen = () => {
  if (props.collapsible) isOpen.value = !isOpen.value;
};

const BASELINE_TYPES = ['schedule', 'cost', 'scope', 'combined'];

const emptyForm = () => ({
  name: '',
  baseline_type: 'combined',
});

const baselines = ref([]);
const isLoading = ref(false);
const showForm = ref(false);
const isSaving = ref(false);
const activatingId = ref(null);
const form = ref(emptyForm());

const loadBaselines = async () => {
  isLoading.value = true;
  try {
    const { data } = await ProjectBaselinesAPI.get(props.projectId);
    baselines.value = data || [];
  } catch (error) {
    baselines.value = [];
  } finally {
    isLoading.value = false;
  }
};

const openForm = () => {
  form.value = emptyForm();
  showForm.value = true;
};

const cancelForm = () => {
  showForm.value = false;
};

const captureBaseline = async () => {
  if (!form.value.name.trim() || isSaving.value) return;
  isSaving.value = true;
  try {
    await ProjectBaselinesAPI.create(props.projectId, form.value);
    showForm.value = false;
    await loadBaselines();
  } catch (error) {
    useAlert(t('PROJECTS.BASELINES.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const activateBaseline = async baseline => {
  if (activatingId.value) return;
  activatingId.value = baseline.id;
  try {
    await ProjectBaselinesAPI.activate(props.projectId, baseline.id);
    useAlert(t('PROJECTS.BASELINES.ACTIVATE_SUCCESS'));
    await loadBaselines();
  } catch (error) {
    useAlert(t('PROJECTS.BASELINES.ACTIVATE_ERROR'));
  } finally {
    activatingId.value = null;
  }
};

const capturedAt = baseline => {
  const raw = baseline.captured_at || baseline.created_at;
  if (!raw) return '—';
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? raw : date.toLocaleDateString();
};

watch(() => props.projectId, loadBaselines);
onMounted(loadBaselines);
</script>

<template>
  <section
    class="flex flex-col overflow-hidden border rounded-xl border-n-weak bg-n-solid-2"
  >
    <header
      class="flex items-center justify-between gap-2 px-4 h-11"
      :class="{ 'border-b border-n-weak': !collapsible || isOpen }"
    >
      <button
        type="button"
        class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12 min-w-0"
        :class="collapsible ? 'cursor-pointer' : 'cursor-default'"
        :disabled="!collapsible"
        @click="toggleOpen"
      >
        <span
          v-if="collapsible"
          class="i-lucide-chevron-right size-4 text-n-slate-10 transition-transform shrink-0"
          :class="{ 'rotate-90': isOpen }"
        />
        <span
          class="i-lucide-git-commit-horizontal size-4 text-n-slate-10 shrink-0"
        />
        <span class="truncate">{{ t('PROJECTS.BASELINES.TITLE') }}</span>
      </button>
      <Button
        v-if="!collapsible || isOpen"
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-camera"
        :label="t('PROJECTS.BASELINES.CAPTURE')"
        @click="openForm"
      />
    </header>

    <div v-show="!collapsible || isOpen" class="flex flex-col gap-3 p-4">
      <!-- Capture form -->
      <div
        v-if="showForm"
        class="flex flex-col gap-3 p-3 border rounded-xl border-n-weak bg-n-background"
      >
        <input
          v-model="form.name"
          type="text"
          :placeholder="t('PROJECTS.BASELINES.FIELDS.NAME')"
          class="w-full h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        />
        <label class="flex flex-col gap-1 text-xs text-n-slate-10">
          {{ t('PROJECTS.BASELINES.FIELDS.TYPE') }}
          <select
            v-model="form.baseline_type"
            class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
          >
            <option v-for="type in BASELINE_TYPES" :key="type" :value="type">
              {{ t(`PROJECTS.BASELINES.TYPES.${type.toUpperCase()}`) }}
            </option>
          </select>
        </label>
        <div class="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            :label="t('PROJECTS.BASELINES.CANCEL')"
            @click="cancelForm"
          />
          <Button
            size="sm"
            color="blue"
            :label="t('PROJECTS.BASELINES.CAPTURE')"
            :is-loading="isSaving"
            :disabled="!form.name.trim()"
            @click="captureBaseline"
          />
        </div>
      </div>

      <!-- Baseline list -->
      <ProjectEmptyState
        v-if="!baselines.length && !isLoading"
        compact
        icon="i-lucide-git-commit-horizontal"
        :title="t('PROJECTS.BASELINES.EMPTY')"
      />
      <ul v-else class="flex flex-col divide-y divide-n-weak/60">
        <li
          v-for="baseline in baselines"
          :key="baseline.id"
          class="flex items-center gap-2.5 py-2.5 group"
        >
          <div class="flex flex-col flex-1 min-w-0 gap-0.5">
            <span class="flex items-center gap-2 text-sm text-n-slate-12">
              <span class="truncate">{{ baseline.name }}</span>
              <span
                v-if="baseline.is_active"
                class="inline-flex items-center px-1.5 py-0.5 text-xs rounded-md bg-n-teal-3 text-n-teal-11 shrink-0"
              >
                {{ t('PROJECTS.BASELINES.ACTIVE') }}
              </span>
            </span>
            <span class="text-xs text-n-slate-10">
              {{
                t(
                  `PROJECTS.BASELINES.TYPES.${(baseline.baseline_type || 'combined').toUpperCase()}`
                )
              }}
              · {{ capturedAt(baseline) }}
            </span>
          </div>
          <Button
            v-if="!baseline.is_active"
            variant="faded"
            color="slate"
            size="xs"
            icon="i-lucide-check"
            :label="t('PROJECTS.BASELINES.ACTIVATE')"
            :is-loading="activatingId === baseline.id"
            class="transition-opacity opacity-0 shrink-0 group-hover:opacity-100"
            @click="activateBaseline(baseline)"
          />
        </li>
      </ul>
    </div>
  </section>
</template>
